import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, KeyboardEvent } from "react";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import { useInteractiveLayer } from "@/components/layout/interactive-layer";
import { pinUserPointer } from "@/components/cursors/user-pointer-tracker";
import { useCursorBarPlacement } from "./use-cursor-bar-placement";
import { useSlashCommandHandler } from "./use-slash-command-handler";
import { useInputTargetPicker } from "./use-input-target-picker";
import { InputTargetPickerPopup } from "./input-target-picker-popup";
import { useInputBarSelection } from "./use-input-bar-selection";
import { useInputBarPaste } from "./use-input-bar-paste";
import { useComposerFileDrop } from "./use-composer-file-drop";
import { InputBarOverlays } from "./input-bar-overlays";
import { useComposerTextareaLayout } from "./use-composer-textarea-layout";
import { InputBarShell } from "./input-bar-shell";
import type { InputBarProps } from "./input-bar-types";
import { CURSOR_BAR_WIDTH } from "./input-bar-metrics";
import { isTauri } from "@/lib/is-tauri";
import { useAgentStore } from "@/stores/agent-store";
import { useLiveDictationDraft } from "./use-live-dictation-draft";
import { useComposerDictation } from "./use-composer-dictation";
import { conversationDisplayName } from "@/lib/conversation-display";
import { StandaloneBarShell } from "./standalone-bar-shell";
import type { ComposerFieldHandle } from "./standalone-bar-field";
import { useStandaloneBarController } from "./use-standalone-bar-controller";

export type { CursorAnchor, InputBarProps } from "./input-bar-types";

const EMPTY_SELECTED_IDS = new Set<string>();

export function InputBar({
  variant = "default",
  revealStage = "idle",
  inputHandleRef,
  value,
  onChange,
  onSubmit,
  isStreaming = false,
  onStop,
  agents = [],
  selectedAgentIds = EMPTY_SELECTED_IDS,
  projectId,
  onOpenSettings,
  placeholder,
  disabled = false,
  submitBlocked = false,
  hasExtraContent = false,
  textOnly = false,
  allowAttachments = false,
  className,
  "data-testid": testId,
  images,
  annotationSummary,
  onRemoveImage,
  onImageFile,
  slashCommands,
  onSlashCommand,
  targetPicker = false,
  focusedAgent = null,
  conversationController,
  workerControls,
  leadingNotice,
  providers,
  onUpdateAgentModel,
  onUpdateAgentEffort,
  cursorAnchor = null,
  cursorPlacement = "cursor",
  onClose,
  compact = false,
  hideTips = false,
  liveDictation = false,
  liveDictationComposerId,
  dictationId,
  dictationSurface,
  nativeSurface = true,
  defaultAgentName,
  companionDisconnected,
  onFixCompanion,
}: InputBarProps) {
  const t = useT(inputBarMessages);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const standaloneFieldRef = useRef<ComposerFieldHandle>(null);
  const standalone = variant === "standalone";
  const inputRef = standalone ? standaloneFieldRef : textareaRef;
  const setStandaloneField = useCallback((handle: ComposerFieldHandle | null) => {
    standaloneFieldRef.current = handle;
    if (inputHandleRef) inputHandleRef.current = handle;
  }, [inputHandleRef]);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileDrop = useComposerFileDrop({ rootRef: containerRef, onFile: !textOnly || allowAttachments ? onImageFile : undefined, disabled });
  submitBlocked ||= Boolean(images?.some((attachment) => attachment.uploading || attachment.uploadError));
  const standaloneBar = useStandaloneBarController({
    enabled: standalone,
    value,
    onChange,
    hasImages: Boolean(images?.length),
    containerRef,
    cursorAnchor,
    cursorPlacement,
    defaultAgentName,
  });
  const latestValueRef = useRef(value);
  const submitPendingRef = useRef(false);
  latestValueRef.current = value;
  const [hirePopupOpen, setHirePopupOpen] = useState(false);
  const [standalonePopoverOpen, setStandalonePopoverOpen] = useState(false);
  const { discardAcceptedDraft } = useLiveDictationDraft({
    enabled: liveDictation,
    value,
    onChange: updateValue,
  });
  const { mic, remember } = useComposerDictation({
    id: dictationId ?? null,
    enabled: !disabled,
    value,
    onChange: updateValue,
    onDelivered: () => {
      const end = latestValueRef.current.length;
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(end, end);
    },
    containerRef,
    surface: dictationSurface,
  });
  const { isMultiLine, syncTextareaHeight } = useComposerTextareaLayout(
    textareaRef,
    value,
  );

  const isCursorMode = cursorAnchor !== null;
  const inputLayer = useInteractiveLayer("input-bar", {
    baseZIndex: isCursorMode ? 70 : 30,
  });
  const resolvedTestId =
    testId ?? (isCursorMode ? "cursor-input-bar" : "input-bar");

  useEffect(() => {
    if (!liveDictation || !liveDictationComposerId || !isTauri()) return;
    const clearAcceptedVoiceDraft = (composerId?: string) => {
      if (composerId === liveDictationComposerId) discardAcceptedDraft();
    };
    let unlisten: (() => void) | null = null;
    let disposed = false;
    // Every monitor's window mirrors dictation, but the send-and-accept runs
    // in whichever window owns the mic: a Tauri event (unlike a DOM one)
    // reaches every window, so this composer hears it wherever it lives.
    void import("@tauri-apps/api/event")
      .then(({ listen }) =>
        listen<{ composerId?: string }>(
          "denker:voice-prompt-accepted",
          (event) => clearAcceptedVoiceDraft(event.payload.composerId),
        ),
      )
      .then((fn) => {
        if (disposed) {
          fn();
          return;
        }
        unlisten = fn;
      })
      .catch(() => {});
    return () => {
      disposed = true;
      unlisten?.();
    };
  }, [discardAcceptedDraft, liveDictation, liveDictationComposerId]);

  useEffect(() => {
    if (isCursorMode && !disabled)
      inputRef.current?.focus({ preventScroll: true });
  }, [disabled, inputRef, isCursorMode]);

  const { selectedIds } = useInputBarSelection({
    agents,
    rawSelectedIds: selectedAgentIds,
    defaultAgentName,
  });

  const routedSlashCommands = useMemo(() => {
    if (textOnly) return [];
    if (!conversationController) return slashCommands;
    return slashCommands?.map((command) =>
      command.type === "command" && command.name === "new"
        ? { ...command, handler: conversationController.onNewConversation }
        : command,
    );
  }, [conversationController, slashCommands, textOnly]);

  const slash = useSlashCommandHandler({
    // Slash menu hidden until its commands are reworked.
    enabled: false,
    value,
    onChange: updateValue,
    slashCommands: routedSlashCommands,
    onSlashCommand,
    inputRef,
  });

  const localNewConversation = !textOnly && !workerControls && conversationController?.canStartNewConversation !== false ? conversationController?.onNewConversation : undefined;
  const picker = useInputTargetPicker({
    enabled: !textOnly && (conversationController ? Boolean(localNewConversation) : targetPicker),
    value,
    onChange: updateValue,
    inputRef,
    chipOffset: standaloneBar.chip?.offset,
  });
  const pickerOpen = picker.trigger !== null && (!localNewConversation || "new".startsWith(picker.trigger.query.trim().toLocaleLowerCase()));
  const isOrchestrating = selectedIds.size >= 3; // Denker + 2+ agents
  // Derive dynamic placeholder from selected agents
  const dynamicPlaceholder = useMemo(() => {
    if (disabled || selectedIds.size === 0) return placeholder ?? t("askAnything");
    if (selectedIds.size === 1) {
      const agent = agents.find((a) => selectedIds.has(a.id));
      return agent ? t("askAgent", { name: agent.name }) : (placeholder ?? t("askAnything"));
    }
    return t("askTeam");
  }, [disabled, selectedIds, agents, placeholder, t]);
  const showTips = !hideTips && !isCursorMode && !isStreaming && !value.trim();

  useEffect(() => {
    if (!isCursorMode || !onClose) return;
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (standalonePopoverOpen || slash.slashOpen || pickerOpen) return;
      e.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    isCursorMode,
    onClose,
    slash.slashOpen,
    pickerOpen,
    standalonePopoverOpen,
  ]);

  const wrapperStyle = useCursorBarPlacement(containerRef, {
    cursorAnchor: standalone ? null : cursorAnchor,
    compactWidth: CURSOR_BAR_WIDTH,
    compact,
    placement: cursorPlacement === "anchor" ? "cursor" : cursorPlacement,
  });

  const { handlePaste, pasteFromSystemClipboard, pasting } = useInputBarPaste({
    autoResize: (element) => {
      if (element instanceof HTMLTextAreaElement) syncTextareaHeight();
    },
    inputRef,
    onChange: updateValue,
    onImageFile: !textOnly || allowAttachments ? onImageFile : undefined,
    disabled,
  });
  submitBlocked ||= pasting;
  const canSubmit =
    (hasExtraContent || value.trim().length > 0 || (images?.length ?? 0) > 0) && !disabled && !submitBlocked;

  function updateValue(next: string, renderedChipOffset?: number) {
    latestValueRef.current = next;
    standaloneBar.updateValue(next, renderedChipOffset);
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    updateValue(e.target.value);
    syncTextareaHeight();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLElement>) {
    if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return;
    if (
      e.key.toLowerCase() === "v" &&
      (e.metaKey || e.ctrlKey) &&
      !e.altKey &&
      !e.shiftKey &&
      isTauri()
    ) {
      e.preventDefault();
      void pasteFromSystemClipboard();
      return;
    }

    if (e.key === "Enter" && e.shiftKey) return;
    if (standalonePopoverOpen && e.key === "Escape") return;
    if (pickerOpen && picker.handleKeyDown(e)) return;
    if (slash.handleKeyDown(e)) return;

    if (e.key === "Escape" && !slash.slashOpen && isCursorMode) {
      e.preventDefault();
      e.stopPropagation();
      onClose?.();
      return;
    }

    if (e.key === "Escape" && !slash.slashOpen && value.trim()) {
      e.preventDefault();
      updateValue("");
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if ((!hasExtraContent && !value.trim() && !images?.length) || disabled || submitBlocked) return;
      if (!slash.tryDispatch()) {
        runSubmit();
      }
    }
  }

  // In cursor mode: pin the user pointer before submit.
  function runSubmit() {
    if (submitPendingRef.current || submitBlocked) return;
    if (
      !textOnly &&
      projectId &&
      agents.length === 0 &&
      useAgentStore.getState().specialistCreationEnabled
    ) {
      setHirePopupOpen(true);
      return;
    }
    if (isCursorMode) {
      const el = containerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        pinUserPointer(
          { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
          4000,
        );
      }
    }
    const submittedValue = value;
    const result = onSubmit();
    if (result instanceof Promise) {
      submitPendingRef.current = true;
      void result.then(
        (sent) => {
          submitPendingRef.current = false;
          if (sent !== false && latestValueRef.current === submittedValue) {
            standaloneBar.markSent();
            updateValue("");
          }
        },
        () => {
          submitPendingRef.current = false;
        },
      );
      return;
    }
    if (result !== false) {
      standaloneBar.markSent();
      updateValue("");
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if ((!hasExtraContent && !value.trim() && !images?.length) || disabled) return;
    if (!slash.tryDispatch()) {
      runSubmit();
    }
  }

  function handleTargetPicked(
    trigger: NonNullable<typeof picker.trigger>,
    row?: Parameters<
      NonNullable<
        React.ComponentProps<typeof InputTargetPickerPopup>["onPicked"]
      >
    >[1],
  ) {
    picker.completePick(trigger);
    if (!standalone || localNewConversation) return;
    const label = row ? `@${conversationDisplayName(row)}` : "@new";
    standaloneBar.placeChip(label, trigger.start, row);
    inputRef.current?.focus();
  }

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
      style={{
        ...(standalone ? standaloneBar.style : wrapperStyle),
        ...inputLayer.style,
      }}
      {...inputLayer.interactionProps}
      data-input-bar-root="true"
      data-testid={resolvedTestId}
      data-interactive="true"
      {...fileDrop.dropProps}
      data-file-dragging={fileDrop.dragging || undefined}
      onPointerEnter={
        standalone ? () => standaloneBar.setExpanded(true) : undefined
      }
    >
      <InputBarOverlays
        standalone={standalone}
        remember={remember}
        focusedAgentId={focusedAgent?.id}
        conversationController={conversationController}
        hirePopupOpen={hirePopupOpen}
        isOrchestrating={isOrchestrating}
        leadingNotice={leadingNotice}
        onOpenSettings={onOpenSettings}
        onSetHirePopupOpen={setHirePopupOpen}
        projectId={projectId}
        selectedIds={selectedIds}
        showTips={showTips}
        slashCommands={routedSlashCommands}
        slashHighlightedIndex={slash.slashIndex}
        slashOpen={slash.slashOpen}
        slashQuery={slash.slashQuery}
        onSlashSelect={slash.selectSlashCommand}
      />
      {pickerOpen && picker.trigger ? (
        <InputTargetPickerPopup
          ref={picker.popupRef}
          trigger={picker.trigger}
          onBeforePick={standalone && !localNewConversation ? standaloneBar.rememberRouting : undefined}
          onNewConversation={localNewConversation}
          onPicked={handleTargetPicked}
          variant={standalone ? "standalone" : "default"}
        />
      ) : null}
      {standalone ? (
        <StandaloneBarShell
          value={value}
          chip={standaloneBar.chip}
          placeholder={standaloneBar.placeholder}
          disabled={disabled}
          canSubmit={canSubmit}
          expanded={standaloneBar.expanded}
          multiline={standaloneBar.multiline}
          nativeSurface={nativeSurface}
          suspended={revealStage !== "idle"}
          revealStage={revealStage}
          dotInsets={standaloneBar.dotInsets}
          images={images ?? []}
          annotationSummary={annotationSummary}
          inputRef={setStandaloneField}
          isStreaming={isStreaming}
          focusedAgent={focusedAgent}
          workerControls={workerControls}
          mic={mic}
          providers={providers}
          onUpdateAgentModel={onUpdateAgentModel}
          onUpdateAgentEffort={onUpdateAgentEffort}
          onStop={onStop}
          companionDisconnected={companionDisconnected}
          onFixCompanion={onFixCompanion}
          onRemoveImage={onRemoveImage}
          onImageFile={!textOnly || allowAttachments ? onImageFile : undefined}
          onChange={updateValue}
          onChipRemoved={standaloneBar.restoreChipRouting}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onSubmit={handleSubmit}
          onEngage={() => standaloneBar.setExpanded(true)}
          popoverOpen={standalonePopoverOpen}
          onPopoverOpenChange={setStandalonePopoverOpen}
        />
      ) : (
        <InputBarShell
          textOnly={textOnly}
          canSubmit={canSubmit}
          compact={compact}
          companionDisconnected={companionDisconnected}
          disabled={disabled}
          dynamicPlaceholder={dynamicPlaceholder}
          focusedAgent={focusedAgent}
          conversationController={conversationController}
          workerControls={workerControls}
          mic={mic}
          images={images ?? []}
          inputRef={textareaRef}
          isMultiLine={isMultiLine}
          nativeSurface={nativeSurface}
          isStreaming={isStreaming}
          onChange={handleChange}
          onFixCompanion={onFixCompanion}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onRemoveImage={onRemoveImage}
          onImageFile={!textOnly || allowAttachments ? onImageFile : undefined}
          onStop={onStop}
          onSubmit={handleSubmit}
          onUpdateAgentEffort={onUpdateAgentEffort}
          onUpdateAgentModel={onUpdateAgentModel}
          providers={providers}
          value={value}
        />
      )}
    </div>
  );
}

export type { InputAgent } from "@/components/panels/agent-stack";
export type { ImageAttachment } from "@/components/panels/image-preview-strip";
